import React, { useEffect, useMemo, useRef, useState } from 'react';
import { CheckIcon, ChevronDownIcon, SearchIcon, XIcon } from 'lucide-react';
export interface SelectOption {
  value: string;
  label: string;
}
interface OptionMultiSelectProps {
  options: SelectOption[];
  selectedValues: string[];
  onChange: (values: string[]) => void;
  placeholder: string;
  error?: boolean;
  disabled?: boolean;
  loading?: boolean;
  allSelected?: boolean;
  onSelectAll?: (selected: boolean) => void;
  showSelectAll?: boolean;
  searchable?: boolean;
  searchPlaceholder?: string;
}
export function OptionMultiSelect({
  options,
  selectedValues,
  onChange,
  placeholder,
  error,
  disabled,
  loading,
  allSelected = false,
  onSelectAll,
  showSelectAll = true,
  searchable = false,
  searchPlaceholder = 'Search...'
}: OptionMultiSelectProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSearchQuery('');
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  useEffect(() => {
    if (isOpen && searchable) {
      searchInputRef.current?.focus();
    }
    if (!isOpen) {
      setSearchQuery('');
    }
  }, [isOpen, searchable]);
  const filteredOptions = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) return options;
    return options.filter(option => option.label.toLowerCase().includes(query));
  }, [options, searchQuery]);
  const handleToggleOption = (value: string) => {
    if (allSelected && onSelectAll) {
      onSelectAll(false);
      onChange([value]);
      return;
    }
    if (selectedValues.includes(value)) {
      onChange(selectedValues.filter(v => v !== value));
    } else {
      onChange([...selectedValues, value]);
    }
  };
  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onSelectAll) onSelectAll(false);
    onChange([]);
  };
  const displayValue = loading ? 'Loading...' : allSelected ? 'All Selected' : selectedValues.length > 0 ? `${selectedValues.length} selected` : placeholder;
  return <div className="relative" ref={containerRef} data-id="element-278">
      <button type="button" disabled={disabled || loading} onClick={() => setIsOpen(!isOpen)} className={`w-full flex items-center justify-between px-3 py-2 text-sm bg-background border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-60 ${error ? 'border-destructive' : 'border-input'}`} data-id="element-279">
        <span className={`truncate ${!allSelected && selectedValues.length === 0 ? 'text-muted-foreground' : 'text-foreground'}`} data-id="element-280">
          {displayValue}
        </span>
        <div className="flex items-center space-x-1 shrink-0" data-id="element-281">
          {(allSelected || selectedValues.length > 0) && <div role="button" tabIndex={0} onClick={handleClear} className="p-0.5 hover:bg-muted rounded-full text-muted-foreground" data-id="element-282">
              <XIcon className="h-3.5 w-3.5" data-id="element-283" />
            </div>}
          <ChevronDownIcon className="h-4 w-4 text-muted-foreground" data-id="element-284" />
        </div>
      </button>

      {selectedValues.length > 0 && !allSelected && <SelectedTags values={selectedValues} options={options} onRemove={value => onChange(selectedValues.filter(v => v !== value))} data-id="element-285" />}

      {isOpen && !disabled && <div className="absolute z-50 w-full mt-1 bg-popover border border-border rounded-md shadow-lg max-h-72 flex flex-col" data-id="element-286">
          {searchable && <div className="p-2 border-b border-border shrink-0" data-id="element-287">
              <div className="relative" data-id="element-288">
                <SearchIcon className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" data-id="element-289" />
                <input ref={searchInputRef} type="text" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} placeholder={searchPlaceholder} className="w-full pl-8 pr-3 py-1.5 text-sm bg-background border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-ring" onClick={e => e.stopPropagation()} data-id="element-290" />
              </div>
            </div>}
          <ul className="py-1 overflow-auto flex-1" data-id="element-291">
            {showSelectAll && onSelectAll && !searchQuery && <>
                <li className="px-3 py-2 text-sm flex items-center cursor-pointer hover:bg-muted" onClick={() => onSelectAll(!allSelected)} data-id="element-292">
                  <div className={`w-4 h-4 mr-2 border rounded flex items-center justify-center ${allSelected ? 'bg-violet-blue border-violet-blue text-white' : 'border-input'}`} data-id="element-293">
                    {allSelected && <CheckIcon className="h-3 w-3" data-id="element-294" />}
                  </div>
                  <span className="font-medium" data-id="element-295">All</span>
                </li>
                <div className="h-px bg-border my-1 mx-2" data-id="element-296" />
              </>}
            {filteredOptions.length === 0 && <li className="px-3 py-2 text-sm text-muted-foreground italic" data-id="element-297">
                {options.length === 0 ? 'No options available' : 'No matches'}
              </li>}
            {filteredOptions.map(option => {
          const isSelected = allSelected || selectedValues.includes(option.value);
          return <li key={option.value} className="px-3 py-2 text-sm flex items-center cursor-pointer hover:bg-muted" onClick={() => handleToggleOption(option.value)} data-id="element-298">
                  <div className={`w-4 h-4 mr-2 border rounded flex items-center justify-center shrink-0 ${isSelected ? 'bg-violet-blue border-violet-blue text-white' : 'border-input'}`} data-id="element-299">
                    {isSelected && <CheckIcon className="h-3 w-3" data-id="element-300" />}
                  </div>
                  <span className="truncate" data-id="element-301">{option.label}</span>
                </li>;
        })}
          </ul>
        </div>}
    </div>;
}
interface StringMultiSelectProps {
  options: string[];
  selectedValues: string[];
  onChange: (values: string[]) => void;
  allSelected?: boolean;
  onSelectAll?: (selected: boolean) => void;
  placeholder: string;
  error?: boolean;
  searchable?: boolean;
}
export function StringMultiSelect({
  options,
  selectedValues,
  onChange,
  allSelected = false,
  onSelectAll,
  placeholder,
  error,
  searchable = false
}: StringMultiSelectProps) {
  return <OptionMultiSelect options={options.map(o => ({
    value: o,
    label: o
  }))} selectedValues={selectedValues} onChange={onChange} allSelected={allSelected} onSelectAll={onSelectAll} showSelectAll={Boolean(onSelectAll)} placeholder={placeholder} error={error} searchable={searchable} data-id="element-302" />;
}
export function SelectedTags({
  values,
  options,
  onRemove
}: {
  values: string[];
  options: SelectOption[];
  onRemove: (value: string) => void;
}) {
  if (!values.length) return null;
  return <div className="flex flex-wrap gap-2 mt-2" data-id="element-303">
      {values.map(v => <span key={v} className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-muted text-xs text-foreground" data-id="element-304">
          {options.find(o => o.value === v)?.label || v}
          <button type="button" onClick={() => onRemove(v)} aria-label="Remove" data-id="element-305">
            <XIcon className="h-3 w-3" data-id="element-306" />
          </button>
        </span>)}
    </div>;
}